import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles, Send, RotateCcw, Calendar, BarChart3, TrendingUp,
  ChevronRight, Zap,
} from 'lucide-react';
import { assistantApi } from '../api/client';
import { fmtNumber, fmtPct, fmtMs, RANGE_PRESETS, rangeToParams } from '../utils/format';

const SUGGESTIONS = [
  { icon: TrendingUp, label: 'What are the top unanswered themes this week?' },
  { icon: BarChart3,  label: 'Which KMS references are over-relied on, and where might that indicate redundancy?' },
  { icon: Sparkles,   label: 'Summarise yesterday’s flagged messages and suggest review priorities.' },
  { icon: Zap,        label: 'What languages dominate Vaicharik queries, and how does that compare to Seva?' },
];

export default function AIAssistant() {
  const [input, setInput] = useState('');
  const [range, setRange] = useState('30d');
  const [messages, setMessages] = useState([]);     // [{ role, content }]
  const [contextSummary, setContextSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Load existing history once
  useEffect(() => {
    assistantApi.history()
      .then((d) => setMessages(d.messages || []))
      .catch(() => { /* ignore */ });
  }, []);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const ask = async (question) => {
    if (!question.trim() || busy) return;
    setError(null);
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setInput('');
    try {
      const params = rangeToParams(range);
      const res = await assistantApi.ask({
        question,
        from: params.from,
        to: params.to,
      });
      setMessages((m) => [...m, { role: 'assistant', content: res.answer }]);
      setContextSummary(res.context_summary || null);
      setLatency(res.latency_ms || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
      // Pop the just-pushed user message so they can edit & retry
      setMessages((m) => m.slice(0, -1));
      setInput(question);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const reset = async () => {
    if (busy) return;
    if (!confirm('Reset this conversation? The history cannot be recovered.')) return;
    try {
      await assistantApi.reset();
      setMessages([]); setContextSummary(null); setLatency(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); ask(input);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px - 56px)', minHeight: 600 }}>
      {/* Header */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <div className="devanagari-watermark" style={{
          position: 'absolute', top: -10, right: -8, fontSize: 110,
          color: 'rgba(194, 108, 27, 0.06)', pointerEvents: 'none',
        }}>
          प्रश्न
        </div>
        <div className="t-eyebrow">SRVC · AI Assistant</div>
        <h1 className="t-display" style={{ fontSize: 36, marginTop: 4, marginBottom: 6 }}>
          Ask the <em style={{ color: 'var(--saffron-deep)' }}>analytics oracle</em>.
        </h1>
        <p style={{ color: 'var(--ink-600)', fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, marginBottom: 0 }}>
          A research console: pose questions about Samvad Mitra’s usage, gaps, and trajectories — answered against live aggregates of the corpus.
        </p>
      </div>

      {/* Context / scope ribbon */}
      <div className="panel" style={{
        padding: '12px 18px', marginBottom: 14,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-600)' }}>
            <Calendar size={13} color="var(--saffron-deep)" />
            <span className="t-eyebrow" style={{ fontSize: 9 }}>Scope</span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {Object.entries(RANGE_PRESETS).map(([k, p]) => (
              <button
                key={k}
                onClick={() => setRange(k)}
                className="btn"
                disabled={busy}
                style={{
                  padding: '5px 10px', fontSize: 11.5,
                  background: range === k ? 'var(--saffron-tint)' : 'transparent',
                  borderColor: range === k ? 'var(--saffron)' : 'var(--ink-200)',
                  color: range === k ? 'var(--saffron-deep)' : 'var(--ink-700)',
                  fontWeight: range === k ? 600 : 500,
                }}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {contextSummary && (
          <div style={{
            display: 'flex', gap: 16, fontSize: 11, color: 'var(--ink-600)',
            alignItems: 'center',
          }}>
            <span>
              <span className="t-eyebrow" style={{ fontSize: 9 }}>Convs</span>{' '}
              <strong>{fmtNumber(contextSummary.headline?.total_conversations || 0)}</strong>
            </span>
            <span>
              <span className="t-eyebrow" style={{ fontSize: 9 }}>Unanswered</span>{' '}
              <strong>{fmtPct(contextSummary.headline?.unanswered_rate || 0)}</strong>
            </span>
            {latency && (
              <span style={{ color: 'var(--ink-400)' }}>
                · last reply in {fmtMs(latency)}
              </span>
            )}
          </div>
        )}

        <button
          className="btn btn-ghost"
          onClick={reset}
          disabled={busy || messages.length === 0}
          style={{ padding: '6px 12px', fontSize: 12 }}
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="panel"
        style={{
          flex: 1, padding: '24px 28px', overflowY: 'auto',
          background: 'var(--paper)', position: 'relative', minHeight: 280,
        }}
      >
        {!messages.length && !busy && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="rule-ornament" style={{ maxWidth: 80, margin: '0 auto 18px', color: 'var(--saffron)' }} />
            <div style={{
              fontFamily: 'var(--serif)', fontStyle: 'italic',
              fontSize: 18, color: 'var(--ink-600)', marginBottom: 26,
              maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5,
            }}>
              Ask anything about how the Dyanakosh is being used — patterns, gaps, anomalies — and receive an answer grounded in live aggregates.
            </div>
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              maxWidth: 720, margin: '0 auto',
            }}>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => ask(s.label)}
                  className="panel"
                  style={{
                    textAlign: 'left',
                    padding: '14px 16px',
                    background: 'var(--paper-2)',
                    border: '1px solid var(--ink-200)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    fontSize: 13, lineHeight: 1.5,
                    transition: 'border-color 120ms ease, transform 120ms ease',
                    color: 'var(--ink-800)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--saffron)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--ink-200)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <s.icon size={16} color="var(--saffron-deep)" style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ flex: 1 }}>{s.label}</span>
                  <ChevronRight size={14} color="var(--ink-400)" style={{ flexShrink: 0, marginTop: 2 }} />
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => {
          const isUser = m.role === 'user';
          return (
            <div key={i} style={{
              display: 'flex',
              flexDirection: isUser ? 'row-reverse' : 'row',
              marginBottom: 18, gap: 14,
            }}>
              <div style={{
                width: 32, height: 32, flexShrink: 0,
                borderRadius: '50%',
                background: isUser ? 'var(--ink-100)' : 'var(--saffron-tint)',
                color: isUser ? 'var(--ink-700)' : 'var(--saffron-deep)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isUser
                  ? <span style={{ fontSize: 12, fontWeight: 600 }}>You</span>
                  : <Sparkles size={15} />}
              </div>
              <div style={{
                maxWidth: '78%', minWidth: 0,
                background: isUser ? 'var(--paper-2)' : 'transparent',
                border: isUser ? '1px solid var(--ink-200)' : 'none',
                borderRadius: 12,
                padding: isUser ? '12px 16px' : '4px 0',
                fontSize: 14, lineHeight: 1.65,
                fontFamily: isUser ? 'var(--sans)' : 'var(--serif)',
                color: 'var(--ink-900)',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {m.content}
              </div>
            </div>
          );
        })}

        {busy && (
          <div style={{ display: 'flex', gap: 14, marginBottom: 18 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'var(--saffron-tint)', color: 'var(--saffron-deep)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={15} />
            </div>
            <div style={{
              fontFamily: 'var(--serif)', fontStyle: 'italic',
              color: 'var(--ink-400)', fontSize: 15,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span className="loader" style={{ width: 12, height: 12, borderWidth: 1.5 }} />
              Consulting the aggregates…
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="panel" style={{
          padding: '10px 14px', borderColor: 'var(--red)',
          background: 'var(--red-tint)', color: 'var(--red)',
          marginTop: 10, fontSize: 13,
        }}>{error}</div>
      )}

      {/* Composer */}
      <div className="panel" style={{
        padding: '12px 16px', marginTop: 12,
        background: 'var(--paper)', position: 'sticky', bottom: 0,
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            className="textarea"
            placeholder="Ask about themes, languages, gaps, references, anomalies…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={busy}
            style={{
              flex: 1, minHeight: 56, maxHeight: 180,
              border: '1px solid var(--ink-200)',
              fontSize: 14, padding: '10px 14px',
              background: 'var(--paper-2)',
            }}
          />
          <button
            onClick={() => ask(input)}
            className="btn btn-primary"
            disabled={busy || !input.trim()}
            style={{ padding: '11px 18px', alignSelf: 'stretch' }}
          >
            <Send size={14} /> {busy ? '…' : 'Send'}
          </button>
        </div>
        <div style={{
          fontSize: 10.5, color: 'var(--ink-400)', marginTop: 6,
          textAlign: 'right',
        }}>
          Press <kbd style={{
            fontFamily: 'var(--mono)', fontSize: 10,
            padding: '1px 5px', background: 'var(--paper-2)',
            border: '1px solid var(--ink-200)', borderRadius: 3,
          }}>Enter</kbd> to send · <kbd style={{
            fontFamily: 'var(--mono)', fontSize: 10,
            padding: '1px 5px', background: 'var(--paper-2)',
            border: '1px solid var(--ink-200)', borderRadius: 3,
          }}>Shift + Enter</kbd> for newline
        </div>
      </div>
    </div>
  );
}
